<template>
    <div class="flex-1">
        <skeleton v-if="planBookingPage.result === 'loading'" />
        <not-found v-else-if="planBookingPage.result === 'missing'" />
        <not-found
            v-else-if="planBookingPage.result === 'invalid'"
            :title="planBookingPage.title"
            :reason="planBookingPage.reason"
        />
        <not-found
            v-else-if="planBookingPage.result === 'inactive'"
            :reason="t('pages.inactivePlan')"
        />
        <purchase-agreement
            v-else-if="planBookingPage.result === 'active'"
            :plan="planBookingPage.plan"
        />
    </div>
</template>

<script setup lang="ts">
import { useRoute } from '#app'
import { computed } from 'vue'
import PurchaseAgreement from '~/components/PurchaseContract/Agreement/PurchaseAgreement.vue'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import Skeleton from 'primevue/skeleton'
import NotFound from '~/components/NotFound/NotFound.vue'
import { useCurrentDate } from '~/composables/use-current-date'

import { usePurchaseAgreement } from '~/components/PurchaseContract/Agreement/use-purchase-agreement'
import { graphql } from '~/resources/graphql'
import { useI18n } from 'vue-i18n'
const route = useRoute()
const slug = computed(() => route.params.slug)
const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

const currentDate = useCurrentDate()
const reactiveDate = computed(() => currentDate.value.getDate())
const query = useRpQuery()
const { data } = useQuery({
    queryKey: ['AgreementsPageQuery', slug, activeFacility, reactiveDate],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query AgreementsPageQuery(
                    $bookingInput: PlanBookingPageInput!
                ) {
                    planBookingPage(input: $bookingInput) {
                        __typename
                        ... on InvalidBookingPage {
                            title
                            reason
                        }
                        ... on Plan {
                            id
                            active
                            terms {
                                language
                            }
                        }
                    }
                }
            `),
            {
                bookingInput: {
                    slug: slug.value,
                    facilityId: activeFacility.value.id,
                    planType: 'AGREEMENT'
                }
            }
        )
})

const purchaseAgreementInput = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    // Only an active plan can drive the agreement locale override.
    if (data.value.planBookingPage?.__typename !== 'Plan') {
        return null
    }

    if (!data.value.planBookingPage.active) {
        return null
    }

    return {
        planId: data.value.planBookingPage.id,
        language: data.value.planBookingPage.terms.language
    }
})
const { plan } = usePurchaseAgreement(purchaseAgreementInput)

const planBookingPage = computed(() => {
    if (data.value === undefined) {
        return {
            result: 'loading' as const
        }
    }

    if (data.value.planBookingPage === null) {
        return {
            result: 'missing' as const
        }
    }

    if (data.value.planBookingPage.__typename !== 'Plan') {
        return {
            result: 'invalid' as const,
            title: data.value.planBookingPage.title,
            reason: data.value.planBookingPage.reason
        }
    }

    if (!data.value.planBookingPage.active) {
        return {
            result: 'inactive' as const
        }
    }

    if (plan.value === undefined) {
        return {
            result: 'loading' as const
        }
    }

    if (plan.value === null) {
        return {
            result: 'missing' as const
        }
    }

    return {
        result: 'active' as const,
        plan: plan.value
    }
})
</script>
